import { WidgetType } from 'widgets/WidgetType';
import { useWidgetsService } from 'widgets/hooks';
import {
    useDispatchUpdateWidgetConfigurationWithoutContext,
    useSelectWidgetConfigState,
} from 'widgets/store/configuration/hooks';
import { WidgetConfigWithTabTitle } from 'widgets/tabTitle/WidgetConfigWithTabTitle';
import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { ToolbarIcon } from '@instinet-analytics/semantic-ui-components';
import { Store } from 'redux';
import Services from 'store/Services';
import { Provider } from 'react-redux';
import { AppContextProvider } from 'app/AppContext';
import { LayoutRendererReactComponent } from 'layoutRenderer/LayoutRendererComponent';
import GoldenLayout from 'golden-layout';
import './EditTitleButton.scss';
import { TextInput } from '@instinet-analytics/semantic-ui-components';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import useOnclickOutside from 'react-cool-onclickoutside';
import { createRoot } from 'react-dom/client';
import { HelpPortal } from 'utils/components/helpPortal/HelpPortal';
import omit from 'lodash/omit';

const EditButtonWrapperClassname = 'edit-title-icon';
const TabEditingTitleClassname = 'editing-title';

/*
 * Adds an EditTitle component to a tab title, immediately before the close button
 */
export function addEditTitleButton(
    tab: GoldenLayout.Tab,
    store: Store,
    services: Services
) {
    const config = tab.contentItem.config as LayoutRendererReactComponent;

    if (!config?.props?.componentType) {
        return;
    }

    const widgetType = config.props.componentType;
    const widgetKey = config.props.componentId;

    const EditTitleWrapperClassName = 'edit-title-section';

    tab.element
        .find('.lm_close_tab')
        .before(`<div class=${EditTitleWrapperClassName}></div>`);

    const container = tab.element.find(`.${EditTitleWrapperClassName}`)[0]!;
    const root = createRoot(container);
    root.render(
        <EditTitleButtonContainer
            services={services}
            store={store}
            tab={tab}
            widgetType={widgetType}
            widgetKey={widgetKey}
        />
    );
}

export function EditTitleButtonContainer(props: {
    store: Store;
    services: Services;
    tab: GoldenLayout.Tab;
    widgetType: WidgetType;
    widgetKey: string;
}) {
    const { store, services, tab, widgetType, widgetKey } = props;

    return (
        <Provider store={store}>
            <AppContextProvider services={services}>
                <EditTitleButton
                    tab={tab}
                    widgetType={widgetType}
                    widgetKey={widgetKey}
                />
            </AppContextProvider>
        </Provider>
    );
}

function EditTitleButton(props: {
    widgetType: WidgetType;
    widgetKey: string;
    tab: GoldenLayout.Tab;
}) {
    const { tab, widgetType, widgetKey } = props;
    const widgetsService = useWidgetsService();
    const locked = useSelectLocked();

    const config = useSelectWidgetConfigState<WidgetConfigWithTabTitle>(
        widgetType,
        widgetKey
    );
    const tabTitle = config?.tabTitle;

    const dispatchUpdateContext =
        useDispatchUpdateWidgetConfigurationWithoutContext<WidgetConfigWithTabTitle>(
            widgetType,
            widgetKey
        );

    const [defaultTitle, setDefaultTitle] = useState(
        widgetsService.getWidgetTitle(widgetType)
    );

    const [inputValue, setInputValue] = useState(tabTitle ?? defaultTitle);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        widgetsService
            .getTitle(widgetType, undefined, omit(config, 'tabTitle'))
            .then(setDefaultTitle);
    }, [widgetsService, widgetType, config, setDefaultTitle]);

    useEffect(() => {
        setInputValue(tabTitle ?? defaultTitle);
    }, [setInputValue, tabTitle, defaultTitle]);

    const handleClose = (dontSave = false) => {
        if (!editing) {
            return;
        }
        const mustSave = !dontSave;
        const newValueIsNotDefault = inputValue.trim() !== defaultTitle;
        const newIsDefaultAndCurrentIsnt =
            inputValue.trim() === defaultTitle && tabTitle !== defaultTitle;

        if (mustSave && (newValueIsNotDefault || newIsDefaultAndCurrentIsnt)) {
            dispatchUpdateContext({
                tabTitle:
                    inputValue.trim() === '' ? undefined : inputValue.trim(),
            });
        }
        setEditing(false);
        (tab.element[0] as HTMLElement).classList.remove(
            TabEditingTitleClassname
        );
    };

    const handleOpen = useCallback(() => {
        setEditing(true);
        (tab.element[0] as HTMLElement).classList.add(TabEditingTitleClassname);
    }, [tab]);

    useEffect(() => {
        tab.element.off('dblclick').on('dblclick', function () {
            if (!locked) {
                handleOpen();
            }
        });
    }, [tab, locked, handleOpen]);

    const ref = useOnclickOutside(() => {
        handleClose();
    });

    const onChanged = (enteredText: string) => {
        setInputValue(enteredText);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            handleClose();
        }
        if (e.key === 'Escape') {
            handleClose(true);
        }
    };

    if (locked) {
        return <></>;
    }

    return (
        <div className="edit-title-panel" ref={ref}>
            {editing && (
                <div className="edit-panel">
                    <TextInput
                        className="edit-title-input"
                        value={inputValue}
                        placeholder="Enter a tab title (optional)"
                        onChange={onChanged}
                        autoFocus={true}
                        onKeyDown={onKeyDown}
                    />
                    <HelpPortal
                        className={'edit-help toolbar'}
                        left={0}
                        width={220}
                        height={100}
                    >
                        <div>
                            Enter an optional tab title.
                            <br /> <br />
                            The text{' '}
                            <span className="loud">
                                {'{symbol}'} and {'{symbolName}'}
                            </span>{' '}
                            will be replaced using the selected or configured
                            symbol.
                        </div>
                    </HelpPortal>
                </div>
            )}
            {!editing && (
                <div
                    className={EditButtonWrapperClassname}
                    onClick={handleOpen}
                >
                    <ToolbarIcon name={'edit'} />
                </div>
            )}
        </div>
    );
}

export function isTargetEditTitleButton(e: React.MouseEvent) {
    const classList: DOMTokenList = (e.target as HTMLElement).classList;
    return classList && classList.contains(EditButtonWrapperClassname);
}

export function editingTitle(tab: GoldenLayout.Tab) {
    return (tab.element[0] as HTMLElement).classList.contains(
        TabEditingTitleClassname
    );
}
